(() => {
  'use strict';
  if (window.__mmAffiliateTrackerV2Initialized) return;
  window.__mmAffiliateTrackerV2Initialized = true;
  const BASE = 'https://pnlenowzewtmwoyasxbq.supabase.co/functions/v1';
  const params = new URLSearchParams(location.search);
  const preview = params.has('preview_theme_id') || Boolean(window.Shopify && (window.Shopify.designMode || window.Shopify.theme?.role === 'unpublished'));
  const ENV = preview ? 'staging' : 'production';
  const API = BASE + (preview ? '/affiliate-phase2-staging' : '/affiliate-phase2');
  const COOKIE = 'mm_aff_session_' + ENV;
  const CACHE = 'mm_aff_attr_v2_' + ENV;
  const REVOCATIONS = 'mm_aff_revoke_v1_' + ENV;
  const routeRoot = window.Shopify?.routes?.root;
  const CART_ROOT = typeof routeRoot === 'string' && /^\/(?:[A-Za-z0-9-]+\/)*$/.test(routeRoot) ? routeRoot : '/';
  const KEYS = ['session', 'click', 'slug', 'campaign', 'creative', 'expires'].map(key => '_mm_affiliate_' + key);
  const EMPTY = Object.fromEntries(KEYS.map(key => [key, '']));
  const validToken = value => /^[A-Za-z0-9_-]{32,100}$/.test(value || '');
  let accountVerified = false, associationInFlight = false, lastAssociation = null, lastAccountToken = null;
  let privacyReady = false, privacyEpoch = 0, lastPermission = null, bootRunning = false, bootQueued = false, referralCaptured = false;
  let revocationJob = null, pendingCapture = null;
  let cartRetryTimer = null, cartRetryCount = 0;
  const pendingRevocations = new Set();
  try {
    const saved = JSON.parse(localStorage.getItem(REVOCATIONS) || '[]');
    if (Array.isArray(saved)) saved.filter(validToken).forEach(token => pendingRevocations.add(token));
  } catch {}
  function marketingAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacyReady && privacy?.marketingAllowed() === true && privacy?.currentVisitorConsent()?.marketing === 'yes';
    } catch { return false; }
  }
  function cookieGet(name) {
    try { const row = document.cookie.split('; ').find(value => value.startsWith(name + '=')); return row ? decodeURIComponent(row.slice(name.length + 1)) : null; } catch { return null; }
  }
  function cookieSet(value, expires) {
    const age = Math.max(0, Math.floor((Date.parse(expires) - Date.now()) / 1000));
    try { document.cookie = COOKIE + '=' + encodeURIComponent(value) + '; Path=/; Max-Age=' + age + '; SameSite=Lax; Secure'; } catch {}
  }
  function persistRevocations() {
    try {
      if (pendingRevocations.size) localStorage.setItem(REVOCATIONS, JSON.stringify([...pendingRevocations]));
      else localStorage.removeItem(REVOCATIONS);
    } catch {}
  }
  function queueRevocation(token) {
    if (!validToken(token)) return;
    pendingRevocations.add(token);
    persistRevocations();
  }
  async function flushRevocations() {
    if (revocationJob) return revocationJob;
    revocationJob = (async () => {
      for (const token of [...pendingRevocations]) {
        try {
          const response = await fetch(API + '/revoke', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({session_token: token}), keepalive: true});
          if (!response.ok) continue;
          const result = await response.json();
          if (result?.ok === true && result?.revoked === true) { pendingRevocations.delete(token); persistRevocations(); }
        } catch {}
      }
      return pendingRevocations.size === 0;
    })();
    try { return await revocationJob; } finally { revocationJob = null; }
  }
  function clearBrowserEvidence() {
    lastAssociation = null;
    pendingCapture = null;
    resetCartRetries();
    try { localStorage.removeItem(CACHE); } catch {}
    cookieSet('', '1970-01-01T00:00:00Z');
  }
  function revokeCurrent() {
    queueRevocation(cookieGet(COOKIE));
    try { queueRevocation(JSON.parse(localStorage.getItem(CACHE) || 'null')?.session_token); } catch {}
    clearBrowserEvidence();
    void flushRevocations();
  }
  function cached() {
    let raw;
    try { raw = localStorage.getItem(CACHE); } catch { return {value: null, expired: false}; }
    if (raw === null) return {value: null, expired: false};
    try {
      const value = JSON.parse(raw);
      if (!value || typeof value !== 'object' || Array.isArray(value) || value.environment !== ENV || !Number.isFinite(Date.parse(value.expires_at)) || Date.parse(value.expires_at) <= Date.now() || KEYS.some(key => value[key] != null && (typeof value[key] !== 'string' || value[key].length > 512))) throw new Error('invalid_cached_referral');
      if (value.pending_capture_key != null && (typeof value.pending_capture_key !== 'string' || value.pending_capture_key.length > 256 || !validToken(value.session_token))) throw new Error('invalid_pending_referral');
      return {value, expired: false};
    } catch { revokeCurrent(); return {value: null, expired: true}; }
  }
  async function syncCart(attrs, epoch = privacyEpoch) {
    if (!attrs) return false;
    const hasReferral = KEYS.some(key => Boolean(attrs[key]));
    const allowed = () => epoch === privacyEpoch && (!hasReferral || (marketingAllowed() && !pendingRevocations.size));
    const matches = cart => cart && typeof cart === 'object' && !Array.isArray(cart)
      && cart.attributes && typeof cart.attributes === 'object' && !Array.isArray(cart.attributes)
      && KEYS.every(key => String(cart.attributes[key] ?? '') === String(attrs[key] ?? ''));
    const readCart = () => fetch(CART_ROOT + 'cart.js', {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store'});
    if (!allowed()) return false;
    try {
      try {
        const current = await readCart();
        if (current.ok && matches(await current.json())) return allowed();
      } catch {}
      if (!allowed()) return false;
      const updated = await fetch(CART_ROOT + 'cart/update.js', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, credentials: 'same-origin', body: JSON.stringify({attributes: attrs})});
      if (!updated.ok || !matches(await updated.json()) || !allowed()) return false;
      // A successful write response alone does not prove the cart retained the evidence.
      const saved = await readCart();
      if (!saved.ok) return false;
      const cart = await saved.json();
      return Boolean(allowed() && matches(cart));
    } catch { return false; }
    finally {
      if (hasReferral && !allowed()) await syncCart(EMPTY, privacyEpoch);
    }
  }
  function completeCachedCapture(value) {
    const completed = {...value};
    delete completed.pending_capture_key;
    if (pendingCapture?.session_token === value.session_token) { pendingCapture = null; resetCartRetries(); }
    try { localStorage.setItem(CACHE, JSON.stringify(completed)); } catch {}
  }
  function resetCartRetries() {
    if (cartRetryTimer !== null) clearTimeout(cartRetryTimer);
    cartRetryTimer = null;
    cartRetryCount = 0;
  }
  function scheduleCartRetry(epoch) {
    if (cartRetryTimer !== null || cartRetryCount >= 2 || !pendingCapture || referralCaptured || epoch !== privacyEpoch || !marketingAllowed() || pendingRevocations.size) return;
    const candidate = pendingCapture, delay = [500, 1500][cartRetryCount++];
    cartRetryTimer = setTimeout(() => {
      cartRetryTimer = null;
      if (pendingCapture !== candidate || referralCaptured || epoch !== privacyEpoch || !marketingAllowed() || pendingRevocations.size) return;
      void runBoot();
    }, delay);
  }
  function cleanUrl() {
    try { const url = new URL(location.href); ['ref', 'campaign', 'cmp', 'creative', 'c'].forEach(key => url.searchParams.delete(key)); history.replaceState(history.state, '', url.pathname + url.search + url.hash); } catch {}
  }
  function verifiedToken() {
    for (const key of ['mmcc-session', 'mm-collector-cloud-session']) {
      try { const value = JSON.parse(localStorage.getItem(key) || 'null'); if (value?.access_token && (!value.expires_at || Number(value.expires_at) * 1000 > Date.now())) return String(value.access_token); } catch {}
    }
    return null;
  }
  async function associate() {
    if (!marketingAllowed() || pendingRevocations.size || !accountVerified || associationInFlight) return;
    const epoch = privacyEpoch, session = cookieGet(COOKIE), token = verifiedToken();
    if (!session || !token || (lastAssociation?.session === session && lastAssociation?.token === token)) return;
    associationInFlight = true;
    try {
      const response = await fetch(API + '/associate', {method: 'POST', headers: {'Content-Type': 'application/json', Authorization: 'Bearer ' + token}, body: JSON.stringify({session_token: session})});
      if (response.ok) { const result = await response.json(); if (epoch === privacyEpoch && marketingAllowed() && result?.associated) lastAssociation = {session, token}; }
    } catch {} finally { associationInFlight = false; }
  }
  async function capture() {
    if (!marketingAllowed() || pendingRevocations.size || referralCaptured) return false;
    const epoch = privacyEpoch, ref = String(params.get('ref') || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{2,39}$/.test(ref)) return false;
    const dimension = value => /^[A-Za-z0-9._:-]{1,80}$/.test(value || '') ? value : null;
    const campaign = dimension(params.get('campaign') || params.get('cmp'));
    const creative = dimension(params.get('creative') || params.get('c'));
    const captureKey = JSON.stringify([ref, campaign, creative]);
    try {
      const previous = cached();
      if (previous.expired) { await syncCart(EMPTY, epoch); await flushRevocations(); }
      const saved = previous.value;
      if (epoch !== privacyEpoch || !marketingAllowed() || pendingRevocations.size) return false;
      let value = pendingCapture?.pending_capture_key === captureKey ? pendingCapture
        : saved?.pending_capture_key === captureKey ? saved : null;
      if (value && Date.parse(value.expires_at) <= Date.now()) { revokeCurrent(); return false; }
      if (!value) {
        const response = await fetch(API + '/click', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({ref, session_token: cookieGet(COOKIE), campaign, creative, landing_url: location.href})});
        if (!response.ok) return false;
        const result = await response.json();
        if (epoch !== privacyEpoch || !marketingAllowed()) { queueRevocation(result?.session_token); await flushRevocations(); return false; }
        if (!result?.ok || !validToken(result.session_token) || !result.cart_attributes || !Number.isFinite(Date.parse(result.expires_at)) || Date.parse(result.expires_at) <= Date.now() || KEYS.some(key => result.cart_attributes[key] != null && (typeof result.cart_attributes[key] !== 'string' || result.cart_attributes[key].length > 512))) return false;
        const attrs = Object.fromEntries(KEYS.map(key => [key, result.cart_attributes[key] || '']));
        value = {...attrs, expires_at: result.expires_at, click_id: result.click_id, session_token: result.session_token, environment: ENV, pending_capture_key: captureKey};
        resetCartRetries();
        try { localStorage.setItem(CACHE, JSON.stringify(value)); } catch {}
      }
      // Retain the accepted click while cart sync is pending, including across reloads
      // when storage is available, so retries do not create another server click.
      pendingCapture = value;
      cookieSet(value.session_token, value.expires_at);
      const attrs = Object.fromEntries(KEYS.map(key => [key, value[key] || '']));
      if (!(await syncCart(attrs, epoch))) { scheduleCartRetry(epoch); return 'pending'; }
      if (epoch !== privacyEpoch || !marketingAllowed() || pendingRevocations.size) return 'pending';
      referralCaptured = true;
      completeCachedCapture(value);
      await associate();
      if (epoch !== privacyEpoch || !marketingAllowed() || pendingRevocations.size) return 'pending';
      cleanUrl(); return 'complete';
    } catch { return false; }
  }
  async function boot() {
    if (!privacyReady) return;
    if (!marketingAllowed()) { revokeCurrent(); await syncCart(EMPTY); return; }
    if (!(await flushRevocations())) { clearBrowserEvidence(); await syncCart(EMPTY); return; }
    try { accountVerified = window.MMCollectorMembership?.getState()?.accountVerified === true; } catch {}
    lastAccountToken = verifiedToken();
    const epoch = privacyEpoch;
    if (await capture()) return;
    if (epoch !== privacyEpoch || !marketingAllowed()) return;
    const state = cached();
    if (state.value) {
      const synced = await syncCart(Object.fromEntries(KEYS.map(key => [key, state.value[key] || ''])));
      if (synced && epoch === privacyEpoch && marketingAllowed() && !pendingRevocations.size && state.value.pending_capture_key) completeCachedCapture(state.value);
    }
    else if (state.expired) await syncCart(EMPTY);
    await associate();
  }
  async function runBoot() {
    if (bootRunning) { bootQueued = true; return; }
    bootRunning = true;
    try { do { bootQueued = false; await boot(); } while (bootQueued); } finally { bootRunning = false; }
  }
  function privacyChanged() {
    if (!privacyReady) return;
    const permission = marketingAllowed();
    if (permission === lastPermission) return;
    const wasAllowed = lastPermission === true;
    lastPermission = permission; privacyEpoch++;
    if (!permission) { if (wasAllowed) referralCaptured = true; revokeCurrent(); void syncCart(EMPTY); return; }
    void runBoot();
  }
  function initializePrivacy() {
    if (typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function' && typeof window.Shopify?.customerPrivacy?.currentVisitorConsent === 'function') { privacyReady = true; privacyChanged(); return; }
    if (typeof window.Shopify?.loadFeatures !== 'function') { revokeCurrent(); void syncCart(EMPTY); return; }
    try { window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.2'}], error => {
      if (error || typeof window.Shopify?.customerPrivacy?.marketingAllowed !== 'function' || typeof window.Shopify?.customerPrivacy?.currentVisitorConsent !== 'function') { revokeCurrent(); void syncCart(EMPTY); return; }
      privacyReady = true; privacyChanged();
    }); } catch { revokeCurrent(); void syncCart(EMPTY); }
  }
  document.addEventListener('visitorConsentCollected', privacyChanged);
  document.addEventListener('mm:cookie-consent-changed', privacyChanged);
  window.addEventListener('online', () => { void flushRevocations().then(() => runBoot()); });
  window.addEventListener('pageshow', event => { if (event.persisted && privacyReady) { privacyChanged(); void runBoot(); } });
  window.addEventListener('mm:membership-change', event => {
    const verified = event.detail?.accountVerified === true, nextToken = verifiedToken();
    const changedAccount = lastAccountToken && lastAccountToken !== nextToken;
    if ((accountVerified && !verified) || changedAccount) { privacyEpoch++; referralCaptured = true; revokeCurrent(); void syncCart(EMPTY); }
    accountVerified = verified; lastAccountToken = verified ? nextToken : null;
    if (verified && !changedAccount) void associate();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePrivacy, {once: true});
  else initializePrivacy();
})();

